import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo } from 'react';
import { T, useT } from '../../translation/src';
import { CreditLedgerEntryFieldsFragment, CreditLedgerEntrySource, CreditLedgerEntryType, InvoicePaymentStatus, InvoiceVia, SuperadminCreditLedgerEntryFieldsFragment } from '../../types/graphqlTypes';
import { useSubscriptionTypeTranslations } from '../../hooks/translations/useSubscriptionTypeTranslations';
import { useFormatDate } from '../../hooks/useFormatDate';
import { dayAfterTermEnd } from '../../utils/dates';
import { formatPrice } from '../../utils/formatPrice';

const useTranslations = () => {
  const scheduled = useT('scheduled', { swc: true });
  const applied = useT('applied', { swc: true });
  const superseded = useT('superseded', { swc: true });
  const cancelled = useT('cancelled', { swc: true });
  const included = useT('included', { swc: true });
  const free = useT('free', { swc: true });
  const paid = useT('paid', { swc: true });
  const pointorama = useT('Pointorama');
  const admin = useT('admin');
  const partiallyPaid = useT('partially paid', { swc: true });
  const unpaid = useT('unpaid', { swc: true });
  return { scheduled, applied, superseded, cancelled, included, free, paid, partiallyPaid, unpaid, pointorama, admin };
};

export const useEntryDescription = (entry: CreditLedgerEntryFieldsFragment): string => {
  const planTranslations = useSubscriptionTypeTranslations();
  // Subscription fees and plan changes carry their plan.
  const plan = entry.subscriptionType ? planTranslations[entry.subscriptionType] : '';
  const purchase = useT('purchase', { swc: true });
  const monthlyAllowance = useT('monthly allowance', { swc: true });
  const downgradeTo = useT('downgrade to {plan}', { args: { plan }, swc: true });
  const upgradeTo = useT('upgrade to {plan}', { args: { plan }, swc: true });
  const planUpgrade = useT('plan upgrade', { swc: true });
  const welcome = useT('welcome project', { swc: true });
  const projectsAdded = useT('projects added', { swc: true });
  const subscription = useT('subscription', { swc: true });
  const planSubscription = useT('{plan} subscription', { args: { plan }, swc: true });
  const renewal = useT('{plan} subscription renewal', { args: { plan }, swc: true });
  const removed = useT('removed by superadmin', { swc: true });
  const referralReward = useT('referral reward', { swc: true });
  const referralMilestone = useT('referral bonus', { swc: true });
  const referralWelcome = useT('referral welcome project', { swc: true });

  switch (entry.source) {
    case CreditLedgerEntrySource.Removed:
      return removed;
    case CreditLedgerEntrySource.Subscription:
      return plan ? planSubscription : subscription;
    case CreditLedgerEntrySource.Renewal:
      return renewal;
    case CreditLedgerEntrySource.PlanDowngrade:
      return downgradeTo;
    case CreditLedgerEntrySource.Upgrade:
    case CreditLedgerEntrySource.ScheduledUpgrade:
      return upgradeTo;
    case CreditLedgerEntrySource.Purchase:
      return purchase;
    case CreditLedgerEntrySource.MonthlyAllowance:
      return monthlyAllowance;
    case CreditLedgerEntrySource.PlanUpgrade:
      return planUpgrade;
    case CreditLedgerEntrySource.Welcome:
      return welcome;
    case CreditLedgerEntrySource.ReferralReward:
      return referralReward;
    case CreditLedgerEntrySource.ReferralMilestone:
      return referralMilestone;
    case CreditLedgerEntrySource.ReferralWelcome:
      return referralWelcome;
    default:
      return entry.note || projectsAdded;
  }
};

interface LedgerRowProps {
  // The superadmin history's rows also carry their Odoo draft invoice; both histories' rows carry the number
  // of the invoice sales sent for them, once it is sent.
  // Both histories also ask whether the row's invoice is paid.
  entry: (CreditLedgerEntryFieldsFragment | SuperadminCreditLedgerEntryFieldsFragment) & { sentInvoiceNumber?: string | null; paymentStatus?: InvoicePaymentStatus | null };
  // Downloads the sent invoice's PDF; downloading is true while this row's is being fetched.
  onDownloadInvoice?: (entryId: string) => void;
  downloading?: boolean;
  // An allowance grant expires when the next period starts, which the subscription
  // header already states as a renewal — only the audit view repeats it per row.
  showExpiry?: boolean;
  // The full ledger: how much of a grant is still unspent.
  showRemaining?: boolean;
  // Superadmin only: make the Odoo draft invoice a priced row is missing; creatingInvoice while this row's is under way.
  onCreateInvoice?: (entryId: string) => void;
  creatingInvoice?: boolean;
}
const LedgerRow_: React.FC<LedgerRowProps> = ({ entry, showExpiry, showRemaining, onCreateInvoice, creatingInvoice, onDownloadInvoice, downloading }) => {
  const translations = useTranslations();
  const { formatDate, formatDay } = useFormatDate();
  const description = useEntryDescription(entry);
  const expired = !!entry.expiresAt && new Date(entry.expiresAt) <= new Date();
  const isSubscription = entry.type === CreditLedgerEntryType.Subscription;
  // Credits a superadmin took off a grant: the one charge the (superadmin) history shows, as a minus.
  const isRemoval = entry.source === CreditLedgerEntrySource.Removed;
  // A scheduled change is dated for the day it takes effect on; an upgrade's row carries the fee it bills then.
  const isPlanChange = entry.source === CreditLedgerEntrySource.PlanDowngrade || entry.source === CreditLedgerEntrySource.ScheduledUpgrade;
  // A later change replaced it (what it sold stays counted), or it was cancelled after its invoice went out: it never takes effect.
  const superseded = !!entry.supersededAt;
  const cancelled = !!entry.cancelledAt;
  const pending = isPlanChange && !superseded && !cancelled && !!entry.effectiveAt && new Date(entry.effectiveAt) > new Date();
  const unpricedChange = isPlanChange && entry.totalPriceCents == null;
  // Allowance credits (and what an upgrade adds to them) come with the subscription; everything else unpriced was free.
  const included = entry.source === CreditLedgerEntrySource.MonthlyAllowance || entry.source === CreditLedgerEntrySource.PlanUpgrade;
  const unpricedLabel = included ? translations.included : translations.free;
  // A renewal's fee is dated for the renewal day, which may be before the day the row was written.
  const isRenewal = entry.source === CreditLedgerEntrySource.Renewal;
  const odooInvoiceUrl = 'odooInvoiceUrl' in entry ? entry.odooInvoiceUrl : undefined;
  const odooInvoiceId = 'odooInvoiceId' in entry ? entry.odooInvoiceId : undefined;
  // A fee Odoo's own subscription invoices: its order is the link, and no draft is ever made for it.
  const viaOdooSubscription = 'invoiceVia' in entry && entry.invoiceVia === InvoiceVia.OdooSubscription;
  const odooOrderUrl = 'odooOrderUrl' in entry ? entry.odooOrderUrl : undefined;
  const odooOrderId = 'odooOrderId' in entry ? entry.odooOrderId : undefined;
  // An upgrade's order is the upsell on the subscription, not a subscription of its own.
  const isUpsell = entry.source === CreditLedgerEntrySource.Upgrade || entry.source === CreditLedgerEntrySource.ScheduledUpgrade;
  const statusLabel = cancelled ? translations.cancelled : superseded ? translations.superseded : pending ? translations.scheduled : undefined;

  return (
    <div className={classNames('flex items-start gap-3 py-2 text-sm border-b border-gray-200 dark:border-gray-700 last:border-b-0', (superseded || cancelled) && 'opacity-70')}>
      <span
        className={classNames(
          'px-2 py-0.5 rounded-full text-xs font-medium font-mono w-12 text-center shrink-0',
          isSubscription
            ? 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
            : isRemoval
              ? 'bg-error-red-200 text-error-red-700 dark:bg-error-red-700 dark:text-error-red-200'
              : 'bg-neon-green-100 text-neon-green-800 dark:bg-neon-green-900 dark:text-neon-green-200',
        )}
      >
        {isSubscription ? '-' : isRemoval ? `-${entry.amount}` : `+${entry.amount}`}
      </span>
      <div className="flex flex-col grow min-w-0">
        <span className={classNames('truncate text-gray-700 dark:text-gray-300', (superseded || cancelled) && 'line-through')}>{description}</span>
        <span className="text-xs text-gray-500">
          {formatDate(entry.createdAt)}
          {(isPlanChange || isRenewal) && entry.effectiveAt && (
            <span className="ml-1">
              · <T _str="effective" />: {formatDay(dayAfterTermEnd(entry.effectiveAt))}
              {statusLabel && !unpricedChange && <span className="ml-1 text-warning-yellow-500">· {statusLabel}</span>}
            </span>
          )}
          {entry.unitPriceCents != null && entry.amount > 0 && (
            <span className="ml-1">
              · {entry.amount} × {formatPrice(entry.unitPriceCents, entry.currency ?? undefined)}
            </span>
          )}
          {/* Only subscription-package credits carry their own expiry; a fee's expiresAt is the term it pays for. */}
          {showExpiry && !isSubscription && entry.expiresAt && (
            <span className={classNames('ml-1', expired && 'text-error-red-500')}>
              · {expired ? <T _str="expired" /> : <T _str="expires" />}: {formatDay(entry.expiresAt)}
            </span>
          )}
          {showRemaining && entry.type === CreditLedgerEntryType.Grant && entry.remaining != null && (
            <span className="ml-1">
              · <T _str="{count} left" args={{ count: entry.remaining }} />
            </span>
          )}
          {/* Who made the row: a customer, or a superadmin (a customer sees "Pointorama" for those). */}
          {entry.grantedByAccount && (
            <span className="ml-1">
              · <T _str="by {name}" args={{ name: entry.grantedByAccount.name ?? translations.pointorama }} />
              {entry.grantedByAccount.isSuperAdmin && entry.grantedByAccount.name && ` (${translations.admin})`}
            </span>
          )}
          {entry.invoice && (
            <span className="ml-1">
              · <T _str="invoice" />: {entry.invoice}
            </span>
          )}
          {entry.sentInvoiceNumber && (
            <span className="ml-1">
              ·{' '}
              {onDownloadInvoice ? (
                <button
                  type="button"
                  disabled={downloading}
                  className={classNames('inline-flex items-center gap-0.5 underline hover:text-neon-green-700 dark:hover:text-neon-green-300', downloading && 'cursor-wait opacity-60')}
                  onClick={() => onDownloadInvoice(entry.id)}
                >
                  <ArrowDownTrayIcon className="h-3 w-3" />
                  {entry.sentInvoiceNumber}
                </button>
              ) : (
                entry.sentInvoiceNumber
              )}
            </span>
          )}
          {odooInvoiceUrl && (
            <span className="ml-1">
              ·{' '}
              <a href={odooInvoiceUrl} target="_blank" rel="noreferrer" className="underline hover:text-neon-green-700 dark:hover:text-neon-green-300">
                Odoo #{odooInvoiceId}
              </a>
            </span>
          )}
          {viaOdooSubscription && (
            <span className="ml-1">
              ·{' '}
              {odooOrderUrl ? (
                <a href={odooOrderUrl} target="_blank" rel="noreferrer" className="underline hover:text-neon-green-700 dark:hover:text-neon-green-300">
                  {isUpsell ? <T _str="Odoo upsell" /> : <T _str="Odoo subscription" />} #{odooOrderId}
                </a>
              ) : (
                <T _str="invoiced by the Odoo subscription" />
              )}
            </span>
          )}
          {/* A row priced at 0 has nothing to invoice; a cancelled one is not to be invoiced; one the Odoo subscription invoices gets no draft. */}
          {!odooInvoiceUrl && !viaOdooSubscription && onCreateInvoice && !cancelled && (entry.totalPriceCents ?? 0) > 0 && (
            <span className="ml-1">
              ·{' '}
              <button
                type="button"
                disabled={creatingInvoice}
                className={classNames('underline hover:text-neon-green-700 dark:hover:text-neon-green-300', creatingInvoice && 'cursor-wait opacity-60')}
                onClick={() => onCreateInvoice(entry.id)}
              >
                <T _str="draft invoice in Odoo" />
              </button>
            </span>
          )}
        </span>
      </div>
      <div className="flex flex-col items-end shrink-0">
        <span className={classNames('font-mono text-sm', (unpricedChange || entry.totalPriceCents == null) && 'text-gray-500', pending && 'text-warning-yellow-500')}>
          {unpricedChange ? (statusLabel ?? translations.applied) : entry.totalPriceCents != null ? formatPrice(entry.totalPriceCents, entry.currency ?? undefined) : isRemoval ? '' : unpricedLabel}
        </span>
        {/* Under the price it is about: green once paid, orange while (part of) it is still due. As tall as the
            line beside it (leading-4), so the row keeps its height. */}
        {entry.paymentStatus && (
          <span
            className={classNames(
              'px-1.5 rounded-full text-[10px] leading-4 font-medium',
              entry.paymentStatus === InvoicePaymentStatus.Paid
                ? 'bg-neon-green-100 text-neon-green-800 dark:bg-neon-green-900 dark:text-neon-green-200'
                : 'bg-warning-yellow-200 text-warning-yellow-700 dark:bg-warning-yellow-700 dark:text-warning-yellow-200',
            )}
          >
            {entry.paymentStatus === InvoicePaymentStatus.Paid ? translations.paid : entry.paymentStatus === InvoicePaymentStatus.Partial ? translations.partiallyPaid : translations.unpaid}
          </span>
        )}
      </div>
    </div>
  );
};

export const LedgerRow = memo(LedgerRow_);
